import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@/api/baseQuery';
import {
  columnsStopLoading,
  rowsCountStopLoading,
  rowsStopLoading,
  selectIsLoadingRows,
  selectLatestQueryMeta,
  setQueryExecutionTime,
  startQueryTimer,
  stepsStartLoading,
  stepsStopLoading,
} from '@/features/query-builder/queryBuilderSlice';
import { useDatasourceId, useSeriesId } from '@/hooks';
import React from 'react';
import { useLoadRowsCount } from '@/features/query-builder/hooks';
import { UUID } from '@/types';
import { GET_CATEGORIES } from '@/features/categories/categoriesApiSlice';
import { TAlert } from '@/features/alerts/types';
import { alertIsoLegacyAlert } from '@/features/alerts/utils/iso';
import { TBucketFeed } from '@/features/bucketFeeds/types';
import { bucketFeedIsoLegacyBucketFeed } from '@/features/bucketFeeds/utils/iso';
import { useSeriesIdContext } from '@/context/SeriesIdContext';
import {
  GET_QUERY_COLUMNS,
  GET_QUERY_ROWS,
  GET_QUERY_STEPS,
  queryBuilderQueriesApi,
} from './queriesApiSlice';
import { latestRequestIds } from './latestRequestIds';

export const GET_SAVED_QUERY = 'getSavedQuery';
export const CREATE_SAVED_QUERY = 'createSavedQuery';
export const UPDATE_SAVED_QUERY = 'updateSavedQuery';
export const REPLACE_SAVED_QUERY = 'replaceSavedQuery';
export const PATCH_SAVED_QUERY = 'patchSavedQuery';
export const GET_SAVED_QUERY_STEPS = 'getSavedQuerySteps';
export const GET_SAVED_QUERY_COLUMNS = 'getSavedQueryColumns';
export const GET_SAVED_QUERY_COLUMN_NAMES = 'getSavedQueryColumnNames';
export const GET_SAVED_QUERY_CACHED_ROWS = 'getSavedQueryCachedRows';

export const GET_SAVED_QUERY_ROWS_COUNT = 'getSavedQueryRowsCount';
export const GET_SAVED_QUERY_CACHE_STATUS = 'getSavedQueryCacheStatus';
export const GET_SAVED_QUERY_CONSUMERS = 'getSavedQueryConsumers';
export const GET_SAVED_QUERY_CONSUMERS_SAMPLES =
  'getSavedQueryConsumersSamples';

export const POLL_GOOGLE_SHEETS_SHARE_STATUS = 'pollGoogleSheetsShareStatus';

const prepareCacheUpsert =
  (adHocQueriesApi: any, savedQueriesApi: any, prevState: any) =>
  (selectArgs: any, upsertArgs: any) =>
  (adHocQueriesKey: any, savedQueriesKey: any) => {
    const latestQueryMeta = selectLatestQueryMeta(prevState);

    const selector =
      'queryId' in latestQueryMeta
        ? adHocQueriesApi.endpoints[adHocQueriesKey]
        : savedQueriesApi.endpoints[savedQueriesKey];

    const result = selector.select(selectArgs)(prevState);

    return savedQueriesApi.util.upsertQueryData(
      savedQueriesKey,
      upsertArgs,
      result.data,
    );
  };

export const queryBuilderSavedQueriesApi = createApi({
  reducerPath: 'queryBuilderSavedQueriesApi',
  baseQuery,
  tagTypes: [
    GET_SAVED_QUERY,
    GET_SAVED_QUERY_STEPS,
    GET_CATEGORIES,
    GET_SAVED_QUERY_CONSUMERS,
    GET_SAVED_QUERY_COLUMNS,
    GET_SAVED_QUERY_CACHED_ROWS,
    GET_SAVED_QUERY_CACHE_STATUS,
  ],
  endpoints: (builder) => ({
    [CREATE_SAVED_QUERY]: builder.mutation<
      { seriesId: UUID },
      { datasourceId: UUID; query: any }
    >({
      query: ({ datasourceId, query }) => ({
        url: `datasources/${datasourceId}/saved-queries`,
        method: 'POST',
        body: query,
      }),
      async onQueryStarted(
        { datasourceId },
        { queryFulfilled, dispatch, getState },
      ) {
        const prevState = getState() as any;
        const result = await queryFulfilled;
        const newSeriesId = result.data.seriesId;
        const latestQueryMeta = selectLatestQueryMeta(prevState);
        const selectArgs = {
          datasourceId,
          ...latestQueryMeta,
        };
        const upsertArgs = { datasourceId, seriesId: newSeriesId };

        const configureArgs = prepareCacheUpsert(
          queryBuilderQueriesApi,
          queryBuilderSavedQueriesApi,
          getState(),
        );

        const generateUpsertAction = configureArgs(selectArgs, upsertArgs);

        await dispatch(
          generateUpsertAction(GET_QUERY_STEPS, GET_SAVED_QUERY_STEPS),
        );

        await dispatch(
          generateUpsertAction(GET_QUERY_COLUMNS, GET_SAVED_QUERY_COLUMNS),
        );

        await dispatch(
          generateUpsertAction(GET_QUERY_ROWS, GET_SAVED_QUERY_CACHED_ROWS),
        );
      },
    }),
    [UPDATE_SAVED_QUERY]: builder.mutation<
      any,
      { datasourceId: UUID; seriesId: UUID; updatedSavedQuery: any }
    >({
      invalidatesTags: [
        GET_SAVED_QUERY_STEPS,
        GET_CATEGORIES,
        GET_SAVED_QUERY_CACHE_STATUS,
      ],
      query: ({ datasourceId, seriesId, updatedSavedQuery }) => ({
        url: `datasources/${datasourceId}/saved-queries/${seriesId}`,
        method: 'PUT',
        body: updatedSavedQuery,
      }),
      async onQueryStarted(
        { datasourceId, seriesId },
        { queryFulfilled, dispatch, getState },
      ) {
        const prevState = getState() as any;
        const isLoadingRows = selectIsLoadingRows(prevState);

        if (isLoadingRows) return;

        await queryFulfilled;
        const latestQueryMeta = selectLatestQueryMeta(prevState);
        const selectArgs = {
          datasourceId,
          ...latestQueryMeta,
        };
        const upsertArgs = { datasourceId, seriesId };

        const configureArgs = prepareCacheUpsert(
          queryBuilderQueriesApi,
          queryBuilderSavedQueriesApi,
          prevState,
        );

        const generateUpdateAction = configureArgs(selectArgs, upsertArgs);

        await dispatch(
          generateUpdateAction(GET_QUERY_STEPS, GET_SAVED_QUERY_STEPS),
        );

        await dispatch(
          generateUpdateAction(GET_QUERY_COLUMNS, GET_SAVED_QUERY_COLUMNS),
        );

        await dispatch(
          generateUpdateAction(GET_QUERY_ROWS, GET_SAVED_QUERY_CACHED_ROWS),
        );
      },
    }),
    [REPLACE_SAVED_QUERY]: builder.mutation<
      { seriesId: UUID },
      { datasourceId: UUID; seriesId: UUID; payload: any }
    >({
      invalidatesTags: [GET_SAVED_QUERY_STEPS, GET_CATEGORIES],
      query: ({ datasourceId, seriesId, payload }) => ({
        url: `datasources/${datasourceId}/saved-queries/${seriesId}/replace`,
        method: 'PUT',
        body: payload,
      }),
      async onQueryStarted(
        { datasourceId },
        { queryFulfilled, dispatch, getState },
      ) {
        const prevState = getState() as any;
        const result = await queryFulfilled;
        const newSeriesId = result.data.seriesId;
        const latestQueryMeta = selectLatestQueryMeta(prevState);
        const selectArgs = {
          datasourceId,
          ...latestQueryMeta,
        };
        const upsertArgs = { datasourceId, seriesId: newSeriesId };

        const configureArgs = prepareCacheUpsert(
          queryBuilderQueriesApi,
          queryBuilderSavedQueriesApi,
          getState(),
        );

        const generateUpsertAction = configureArgs(selectArgs, upsertArgs);

        await dispatch(
          generateUpsertAction(GET_QUERY_STEPS, GET_SAVED_QUERY_STEPS),
        );

        await dispatch(
          generateUpsertAction(GET_QUERY_COLUMNS, GET_SAVED_QUERY_COLUMNS),
        );

        await dispatch(
          generateUpsertAction(GET_QUERY_ROWS, GET_SAVED_QUERY_CACHED_ROWS),
        );
      },
    }),
    [PATCH_SAVED_QUERY]: builder.mutation<any, any>({
      query: ({ datasourceId, seriesId, updatedSavedQuery }) => ({
        url: `datasources/${datasourceId}/saved-queries/${seriesId}`,
        method: 'PATCH',
        body: updatedSavedQuery,
      }),
      invalidatesTags: [GET_SAVED_QUERY_STEPS, GET_CATEGORIES, GET_SAVED_QUERY],
    }),
    [GET_SAVED_QUERY]: builder.query<any, any>({
      query: ({ datasourceId, seriesId }) =>
        `datasources/${datasourceId}/saved-queries/${seriesId}`,
      providesTags: [GET_SAVED_QUERY],
    }),
    [GET_SAVED_QUERY_STEPS]: builder.query<any, any>({
      query: ({ datasourceId, seriesId, asSQL = false }) =>
        `datasources/${datasourceId}/saved-queries/${seriesId}/steps?asSQL=${asSQL}`,
      transformResponse: (data: any, _meta, { asSQL }) =>
        asSQL ? data : data.steps,
      async onQueryStarted({ asSQL }, { dispatch, queryFulfilled, requestId }) {
        if (asSQL) return;

        latestRequestIds.steps = requestId;
        dispatch(startQueryTimer());
        dispatch(stepsStartLoading());

        await queryFulfilled;

        if (latestRequestIds.steps === requestId) {
          dispatch(stepsStopLoading());
        }
      },
      providesTags: [GET_SAVED_QUERY_STEPS],
    }),
    [GET_SAVED_QUERY_COLUMNS]: builder.query<any, any>({
      query: ({ datasourceId, seriesId, layerInEditModeId }) =>
        `datasources/${datasourceId}/saved-queries/${seriesId}/columns${
          layerInEditModeId ? `?layerInEditModeId=${layerInEditModeId}` : ''
        }`,
      async onQueryStarted(_args, { dispatch, queryFulfilled, requestId }) {
        latestRequestIds.columns = requestId;
        try {
          await queryFulfilled;
        } catch (_e) {
          dispatch(rowsStopLoading());
          dispatch(rowsCountStopLoading());
        }
        if (latestRequestIds.columns === requestId) {
          dispatch(columnsStopLoading());
        }
      },
      providesTags: [GET_SAVED_QUERY_COLUMNS],
    }),
    [GET_SAVED_QUERY_CACHED_ROWS]: builder.query<any, any>({
      query: ({ datasourceId, seriesId }) =>
        `datasources/${datasourceId}/saved-queries/${seriesId}/rows`,
      async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
        latestRequestIds.rows = requestId;

        try {
          const { data } = await queryFulfilled;

          if (latestRequestIds.rows === requestId) {
            dispatch(setQueryExecutionTime(Date.now()));
            dispatch(rowsStopLoading());

            if (data.dataTable._rows.length <= 100) {
              dispatch(rowsCountStopLoading());
            }
          }
        } catch (_e) {
          if (latestRequestIds.rows === requestId) {
            dispatch(rowsStopLoading());
            dispatch(rowsCountStopLoading());
          }
        }
      },
      providesTags: [GET_SAVED_QUERY_CACHED_ROWS],
    }),
    [GET_SAVED_QUERY_ROWS_COUNT]: builder.query<any, any>({
      query: ({ datasourceId, seriesId }) =>
        `datasources/${datasourceId}/saved-queries/${seriesId}/rows/count`,
      async onQueryStarted(_arg, { dispatch, queryFulfilled, requestId }) {
        latestRequestIds.rowsCount = requestId;

        try {
          await queryFulfilled;

          if (latestRequestIds.rowsCount === requestId) {
            dispatch(rowsCountStopLoading());
          }
        } catch (_e) {
          //
        }
      },
    }),
    [GET_SAVED_QUERY_CACHE_STATUS]: builder.query<any, any>({
      query: ({ datasourceId, seriesId }) =>
        `datasources/${datasourceId}/saved-queries/${seriesId}/status`,
      providesTags: [GET_SAVED_QUERY_CACHE_STATUS],
    }),
    [GET_SAVED_QUERY_COLUMN_NAMES]: builder.query<any, any>({
      query: ({ datasourceId, seriesId }) =>
        `datasources/${datasourceId}/saved-queries/${seriesId}/column-names`,
    }),
    [POLL_GOOGLE_SHEETS_SHARE_STATUS]: builder.query<
      { shared: boolean },
      { datasourceId: UUID; savedQueryId: UUID }
    >({
      query: ({ datasourceId, savedQueryId }) =>
        `datasources/${datasourceId}/saved-queries/${savedQueryId}/poll-google-sheets-share-status`,
    }),
    [GET_SAVED_QUERY_CONSUMERS]: builder.query<
      {
        slackAlerts: TAlert[];
        emailAlerts: TAlert[];
        zaps: any[];
        bucketFeeds: TBucketFeed[];
        sharedViaGoogleSheets: boolean;
        sharedViaOtherTools: boolean;
      },
      { datasourceId: UUID; savedQueryId: UUID }
    >({
      query: ({ datasourceId, savedQueryId }) =>
        `datasources/${datasourceId}/views/${savedQueryId}/share`,
      providesTags: [GET_SAVED_QUERY_CONSUMERS],
      // TODO: migrate API to kotlin and change keys names there.
      transformResponse: (data: any) => ({
        slackAlerts: data._alerts.reduce((slackAlerts: any[], alert: any) => {
          if (alert._triggerType === 'slack') {
            slackAlerts.push(alertIsoLegacyAlert.reverseGet(alert));
          }
          return slackAlerts;
        }, []),
        emailAlerts: data._alerts.reduce((emailAlerts: any[], alert: any) => {
          if (alert._triggerType === 'email') {
            emailAlerts.push(alertIsoLegacyAlert.reverseGet(alert));
          }
          return emailAlerts;
        }, []),
        zaps: data._alerts.reduce((zaps: any[], alert: any) => {
          if (alert._triggerType === 'zapier') {
            zaps.push(alert);
          }
          return zaps;
        }, []),
        bucketFeeds: data._bucketFeeds.map(
          bucketFeedIsoLegacyBucketFeed.reverseGet,
        ),
        sharedViaGoogleSheets: data._isSheetsShareable,
        sharedViaOtherTools: data._isOtherShareable,
      }),
    }),
    [GET_SAVED_QUERY_CONSUMERS_SAMPLES]: builder.query<any, any>({
      query: ({ datasourceId, savedQueryId }) => ({
        url: `datasources/${datasourceId}/views/${savedQueryId}/samples`,
      }),
    }),
  }),
});

export const {
  useCreateSavedQueryMutation,
  useUpdateSavedQueryMutation,
  useReplaceSavedQueryMutation,
  useGetSavedQueryStepsQuery,
  useGetSavedQueryColumnsQuery,
  useGetSavedQueryCachedRowsQuery,
  useGetSavedQueryRowsCountQuery,
  useGetSavedQueryCacheStatusQuery,
  usePatchSavedQueryMutation,
  useGetSavedQueryConsumersQuery,
  useGetSavedQueryColumnNamesQuery,
  useGetSavedQueryConsumersSamplesQuery,
  usePollGoogleSheetsShareStatusQuery,
} = queryBuilderSavedQueriesApi;

export const useSavedQuery = () => {
  const seriesId = useSeriesIdContext();
  const { data: listedSavedQuery } = useGetSavedQuery(!seriesId, seriesId!);
  const { data: singleSavedQuery } = useGetSavedQuery(!!seriesId);

  return singleSavedQuery || listedSavedQuery;
};

export const usePollGoogleSheetsShareStatus = () => {
  const datasourceId = useDatasourceId();
  const savedQuery = useSavedQuery();

  const savedQueryId = savedQuery?.id;

  const { data } = usePollGoogleSheetsShareStatusQuery(
    { datasourceId, savedQueryId },
    { skip: !savedQueryId, pollingInterval: 1000 },
  );

  return data ?? { shared: false };
};

export const useGetSavedQueryConsumers = () => {
  const datasourceId = useDatasourceId();
  const savedQuery = useSavedQuery();

  const savedQueryId = savedQuery?.id;

  return useGetSavedQueryConsumersQuery(
    { datasourceId, savedQueryId },
    { skip: !savedQueryId },
  );
};

export const useGetBucketFeeds = () => {
  const { data, ...rest } = useGetSavedQueryConsumers();

  return {
    bucketFeeds: data?.bucketFeeds ?? [],
    ...rest,
  } as const;
};

export const useGetSlackAlerts = () => {
  const { data, ...rest } = useGetSavedQueryConsumers();

  return {
    slackAlerts: data?.slackAlerts ?? [],
    ...rest,
  } as const;
};

export const useGetZaps = () => {
  const { data, ...rest } = useGetSavedQueryConsumers();

  return {
    zaps: data?.zaps ?? [],
    ...rest,
  } as const;
};

export const useGetSharedViaGoogleSheets = () => {
  const { data, ...rest } = useGetSavedQueryConsumers();

  return {
    sharedViaGoogleSheets: data?.sharedViaGoogleSheets ?? false,
    ...rest,
  } as const;
};

export const useGetEmailAlerts = () => {
  const { data, ...rest } = useGetSavedQueryConsumers();

  return {
    emailAlerts: data?.emailAlerts ?? [],
    ...rest,
  } as const;
};

export const useGetSavedQuery = (skip = false, seriesIdArg?: UUID) => {
  const datasourceId = useDatasourceId();
  const seriesIdParam = useSeriesId();

  const seriesId = seriesIdArg || seriesIdParam;

  return queryBuilderSavedQueriesApi.useGetSavedQueryQuery(
    {
      datasourceId,
      seriesId,
    },
    {
      skip: !seriesId || skip,
    },
  );
};

export const usePollSavedQueryCacheStatus = () => {
  const datasourceId = useDatasourceId();
  const seriesId = useSeriesId();
  const readyRef = React.useRef(false);

  const { data: rowCountData } = useLoadRowsCount();

  const skip = !seriesId || readyRef.current;

  React.useEffect(() => {
    readyRef.current = false;
  }, [seriesId]);

  const { data, isUninitialized } = useGetSavedQueryCacheStatusQuery(
    { datasourceId, seriesId },
    {
      pollingInterval: readyRef.current ? 0 : 1000,
      skip,
    },
  );

  readyRef.current = data?._status_loaded ?? false;

  return {
    ...data,
    isLoading: !readyRef.current,
    isUninitialized,
    totalRowCount: rowCountData?._rowCount,
  };
};

export const useGetSavedQueryStepsAsSQL = () => {
  const datasourceId = useDatasourceId();
  const seriesIdFromCtx = useSeriesIdContext();
  const seriesIdParam = useSeriesId();

  const seriesId = seriesIdParam || seriesIdFromCtx;

  return useGetSavedQueryStepsQuery(
    { datasourceId, seriesId, asSQL: true },
    { skip: !seriesId },
  );
};

export const useGetConsumersSamples = () => {
  const datasourceId = useDatasourceId();
  const savedQuery = useSavedQuery();
  const savedQueryId = savedQuery?.id;

  const { data, ...rest } = useGetSavedQueryConsumersSamplesQuery(
    { datasourceId, savedQueryId },
    { skip: !savedQueryId },
  );

  return { samples: data ?? [], ...rest };
};

export const useGetColumnNames = () => {
  const datasourceId = useDatasourceId();
  const savedQuery = useSavedQuery();

  const seriesId = savedQuery?.seriesId;

  const { data, ...rest } = useGetSavedQueryColumnNamesQuery(
    {
      datasourceId,
      seriesId,
    },
    { skip: !seriesId },
  );

  return { columnNames: data ?? [], ...rest };
};
